import { useNavigate, useParams } from 'react-router-dom';
import { useFetch } from '../../hooks/useFetch';
import LoadingContent from '../../components/LoadingContent';
import { useAuth } from '../../context/AuthContext';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { differenceInMonths, isSameDay, parseISO, startOfDay } from 'date-fns';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import useHandleErrors from '../../hooks/useHandleErrors';
import { DateCalendar, PickersDay } from '@mui/x-date-pickers';
import { CheckCircle, CheckCircleOutline, Close, ArrowBack } from '@mui/icons-material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import * as Yup from 'yup';
import server from '../../global/server';
import ConflictDialog from '../../components/ConflictDialog';

const SectionCard = ({ children, sx = {} }) => (
    <Box sx={{
        width: '100%',
        bgcolor: 'white',
        borderRadius: '16px',
        border: '1px solid #e8e8e8',
        p: { xs: '20px 16px', sm: '28px 24px' },
        ...sx,
    }}>
        {children}
    </Box>
);

const SectionTitle = ({ children }) => (
    <Typography variant="subtitle1" fontWeight={600} color="#333" sx={{ mb: 1.5 }}>
        {children}
    </Typography>
);

const Schema = Yup.object({
    schedule_date: Yup.string().required(),
});

const MeetingDetails = () => {
    const params = useParams();
    const { user } = useAuth();
    const [errs, setErrs] = useState();
    useHandleErrors(errs);
    const [datesDialog, setDatesDialog] = useState(false);
    const [successData, setSuccessData] = useState(null);
    const [conflictData, setConflictData] = useState(null);
    const [conflictOpen, setConflictOpen] = useState(false);
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigation = useNavigate();
    const { data, mutate, isLoading } = useFetch('/search', { id: Number(params.id) });
    const meeting = data?.meetings ? data.meetings.find(m => m.id === Number(params.id)) : data;

    const isOwner = meeting?.Profile?.User?.id === user?.id;

    const { values, handleSubmit, setFieldValue, errors } = useFormik({
        initialValues: {
            schedule_date: '',
            meeting_schedule_id: '',
            meeting_id: params.id,
        },
        validationSchema: Schema,
        validateOnMount: true,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);

                // Verificar conflitos de horario
                const { data: conflictCheck } = await server.get('/booking/conflicts', {
                    params: { datetime: values.schedule_date },
                });
                if (conflictCheck.hasConflict) {
                    setConflictData(conflictCheck);
                    setConflictOpen(true);
                    setSubmitting(false);
                    return;
                }

                await proceedWithBooking(values);
            } catch (error) {
                setErrs(error);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const proceedWithBooking = async (vals) => {
        const v = vals || values;
        if (meeting.free === true) {
            const scheduleDateTime = new Date(v.schedule_date).toISOString();
            const body = {
                meeting_id: v.meeting_id,
                datetime: scheduleDateTime,
                payment_method: 'free',
            };
            const { data } = await server.post('/booking', body, {
                timeout: 50000,
                timeoutErrorMessage: 'Tempo de operação esgotado',
            });
            setDatesDialog(false);
            setSuccessData(data.data);
        } else {
            navigation(`/encontrar/${params.id}/pagamento`, { state: v });
        }
    };

    // Revalida ao abrir a tela: o horario pode ter sido vendido ou cancelado
    // desde que a listagem foi carregada. `mutate` do SWR e estavel.
    useEffect(() => { mutate(); }, [mutate]);

    if (isLoading) return <LoadingContent />;

    if (!meeting) {
        return (
            <Box sx={{ maxWidth: 720, mx: 'auto', p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">Encontro não encontrado.</Typography>
            </Box>
        );
    }

    // A tela oferecia todo horario do encontro, inclusive os que o servidor
    // recusa: ja vendido, cancelado pelo anfitriao ou no passado. Dava a compra
    // por disponivel e so na confirmacao vinha o erro. Em sorteio o horario
    // continua aberto mesmo vendido, porque varias pessoas se inscrevem nele.
    const horarioDisponivel = schedule =>
        Boolean(schedule?.datetime)
        && !schedule.canceled_at
        && !schedule.finished_at
        && new Date(schedule.datetime) > new Date()
        && (meeting?.raffle || !schedule.purchased);

    const horariosAbertos = (meeting?.meeting_schedule || []).filter(horarioDisponivel);

    const scheduled_dates = horariosAbertos.map(schedule =>
        startOfDay(new Date(schedule.datetime)).toISOString(),
    );

    const currentHours = horariosAbertos.filter(item => {
        if (Boolean(values.currentDate) && isSameDay(values.currentDate, parseISO(item.datetime))) return item;
        return null;
    });

    const hostUser = meeting?.Profile?.User;

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: 720,
            mx: 'auto',
            p: { xs: '16px 12px 32px', sm: '24px 16px 48px' },
            gap: 3,
        }}>
            {/* Voltar */}
            <Button startIcon={<ArrowBack />} onClick={() => navigation(-1)} sx={{ textTransform: 'none', color: '#666', alignSelf: 'flex-start' }}>
                Voltar
            </Button>

            {/* Header do anfitriao */}
            <SectionCard>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                        src={hostUser?.profileImage}
                        alt={hostUser?.name}
                        sx={{
                            width: 64,
                            height: 64,
                            border: '2px solid #e0e0e0',
                            cursor: 'pointer',
                        }}
                        onClick={() => navigation(`/anfitriao/@${hostUser?.username}`)}
                    >
                        {hostUser?.name?.[0]}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                            {meeting.title}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            onClick={() => navigation(`/anfitriao/@${hostUser?.username}`)}
                        >
                            Anfitrião: @{hostUser?.username}
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                    {meeting.free
                        ? <Chip size="small" label="Gratuito" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 500 }} />
                        : <Chip size="small" label={`R$ ${Number(meeting.pricing).toFixed(2).replace('.', ',')}`} sx={{ bgcolor: '#fef3c7', color: '#b45309', fontWeight: 600 }} />
                    }
                    <Chip size="small" label={meeting.raffle ? 'Seleção' : 'Agendamento'} sx={{ bgcolor: '#e6f3f1', color: '#006a61', fontWeight: 500 }} />
                    <Chip size="small" label={meeting.visible_public ? 'Publico' : 'Privado'} sx={{ bgcolor: '#e6f3f1', color: '#006a61', fontWeight: 500 }} />
                </Stack>

                {/* Horarios disponiveis */}
                {(() => {
                    const futureSchedules = (meeting?.meeting_schedule || [])
                        .filter(s => s.datetime && new Date(s.datetime) > new Date())
                        .sort((a, b) => new Date(a.datetime) - new Date(b.datetime));

                    if (!futureSchedules.length) return null;

                    const grouped = futureSchedules.reduce((acc, s) => {
                        const dateKey = startOfDay(new Date(s.datetime)).toISOString();
                        if (!acc[dateKey]) acc[dateKey] = [];
                        acc[dateKey].push(s);
                        return acc;
                    }, {});

                    return (
                        <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid #f0f0f0' }}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                                Horários disponíveis
                            </Typography>
                            <Stack spacing={1.5} sx={{ mt: 1 }}>
                                {Object.entries(grouped).map(([dateKey, schedules]) => {
                                    const dt = new Date(dateKey);
                                    const label = dt.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' });
                                    return (
                                        <Box key={dateKey}>
                                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
                                                <CalendarTodayIcon sx={{ fontSize: 14, color: '#999' }} />
                                                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                                                    {label}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" flexWrap="wrap" gap={0.75}>
                                                {schedules.map(s => (
                                                    <Chip
                                                        key={s.id}
                                                        size="small"
                                                        icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                                                        label={new Date(s.datetime).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                                        sx={{ bgcolor: '#f5f5f5', fontWeight: 500, '& .MuiChip-icon': { color: '#888' } }}
                                                    />
                                                ))}
                                            </Stack>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </Box>
                    );
                })()}

                {/* Tags do encontro */}
                {(meeting.topic || meeting.subtopic) && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #f0f0f0' }}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                            Tags
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                            {meeting.topic && <Chip size="small" label={meeting.topic} sx={{ bgcolor: '#f0f0f0', color: '#555', fontWeight: 500 }} />}
                            {meeting.subtopic && <Chip size="small" label={meeting.subtopic} sx={{ bgcolor: '#f0f0f0', color: '#555', fontWeight: 500 }} />}
                        </Stack>
                    </Box>
                )}
            </SectionCard>

            {/* Sobre o anfitriao */}
            {hostUser?.description && (
                <SectionCard>
                    <SectionTitle>Sobre o anfitrião</SectionTitle>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {hostUser.description}
                    </Typography>
                </SectionCard>
            )}

            {/* Proposta do encontro */}
            <SectionCard>
                <SectionTitle>Proposta do encontro</SectionTitle>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {meeting.description}
                </Typography>
            </SectionCard>

            {/* Aviso sobre a selecao */}
            {meeting.raffle && !isOwner && (
                <Alert severity="info" sx={{ width: '100%', borderRadius: '12px', '& .MuiAlert-message': { fontSize: '0.85rem' } }}>
                    <strong>Este encontro é na modalidade seleção.</strong> Ao adquirir, você concorre a uma vaga para conversar diretamente com o anfitrião.
                    Caso não seja selecionado, você terá acesso exclusivo a transmissão ao vivo como espectador.
                </Alert>
            )}

            {/* Alerta se for o dono */}
            {isOwner && (
                <Alert
                    severity="info"
                    sx={{ width: '100%', borderRadius: '12px' }}
                >
                    <strong>Este e o seu encontro!</strong> Você esta visualizando como fica para os convivas. Não e possível marcar encontro consigo mesmo.
                </Alert>
            )}

            {/* Botao de acao */}
            <Button
                variant="contained"
                fullWidth
                disabled={!scheduled_dates.length || isOwner}
                onClick={() => setDatesDialog(true)}
                sx={{
                    bgcolor: '#006a61',
                    borderRadius: '8px',
                    height: 48,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    '&:hover': { bgcolor: '#005a52' },
                }}
            >
                {isOwner
                    ? 'Você não pode marcar o próprio encontro'
                    : !scheduled_dates.length
                        ? 'Sem horários disponíveis'
                        : 'Marcar encontro'}
            </Button>

            {/* Dialog de selecao de data */}
            <Dialog
                open={datesDialog}
                fullScreen={mobile}
                fullWidth
                maxWidth="sm"
                onClose={() => setDatesDialog(false)}
                PaperProps={{ sx: { borderRadius: mobile ? 0 : '16px' } }}
            >
                <DialogTitle sx={{ fontWeight: 600, pr: 5 }}>
                    Selecione uma data e horário
                </DialogTitle>
                <IconButton
                    onClick={() => setDatesDialog(false)}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <Close />
                </IconButton>

                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={7}>
                            <DateCalendar
                                disablePast
                                shouldDisableDate={e => differenceInMonths(e, new Date()) > 0}
                                slots={{
                                    day: ({ day, focusRipple, ...rest }) => {
                                        const dayISO = startOfDay(day).toISOString();
                                        const hasSchedule = scheduled_dates.includes(dayISO);
                                        const isSelected = Boolean(values?.currentDate) &&
                                            startOfDay(values.currentDate).toISOString() === dayISO;

                                        return (
                                            <PickersDay
                                                {...rest}
                                                onDaySelect={e => setFieldValue('currentDate', e)}
                                                day={day}
                                                sx={{
                                                    bgcolor: isSelected ? '#006a61' : hasSchedule ? '#e6f3f1' : undefined,
                                                    color: isSelected ? 'white' : hasSchedule ? '#006a61' : undefined,
                                                    fontWeight: hasSchedule ? 600 : undefined,
                                                    '&:hover': {
                                                        bgcolor: isSelected ? '#005a52' : hasSchedule ? '#cce8e4' : undefined,
                                                    },
                                                }}
                                            />
                                        );
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                                Horários disponíveis
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                {currentHours?.map(({ datetime }) => {
                                    const iso = new Date(datetime).toISOString();
                                    const isChosen = values.schedule_date === iso;
                                    return (
                                        <Chip
                                            key={datetime}
                                            icon={isChosen ? <CheckCircle sx={{ fontSize: 16 }} /> : <AccessTimeIcon sx={{ fontSize: 16 }} />}
                                            label={new Date(datetime).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                            onClick={() => setFieldValue('schedule_date', iso)}
                                            sx={{
                                                bgcolor: isChosen ? '#006a61' : '#f5f5f5',
                                                color: isChosen ? 'white' : '#333',
                                                fontWeight: 500,
                                                '& .MuiChip-icon': { color: isChosen ? 'white' : '#888' },
                                                '&:hover': { bgcolor: isChosen ? '#005a52' : '#eee' },
                                            }}
                                        />
                                    );
                                })}
                                {!Boolean(values.currentDate) && (
                                    <Typography variant="body2" color="text.secondary">
                                        Selecione um dia no calendário
                                    </Typography>
                                )}
                                {Boolean(values.currentDate) && !currentHours.length && (
                                    <Typography variant="body2" color="text.secondary">
                                        Nenhum horário disponível neste dia
                                    </Typography>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        variant="contained"
                        disabled={Boolean(Object.values(errors).length)}
                        onClick={handleSubmit}
                        fullWidth
                        sx={{
                            bgcolor: '#006a61',
                            borderRadius: '8px',
                            height: 44,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#005a52' },
                        }}
                    >
                        {meeting.free ? 'Confirmar encontro' : 'Ir para o pagamento'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConflictDialog
                open={conflictOpen}
                onClose={() => setConflictOpen(false)}
                conflicts={conflictData}
                onResolved={() => proceedWithBooking()}
            />

            <Dialog
                open={Boolean(successData)}
                PaperProps={{ sx: { borderRadius: '16px', maxWidth: 400 } }}
            >
                <DialogContent sx={{ textAlign: 'center', p: { xs: 3, sm: 4 } }}>
                    <CheckCircleOutline sx={{ fontSize: 64, color: '#2e7d32', mb: 2 }} />
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                        Agendamento realizado com sucesso!
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                        Seu encontro com <strong>@{hostUser?.username}</strong> foi agendado.
                        Você pode acompanhar os detalhes na sua lista de encontros.
                    </Typography>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => navigation(`/encontros/${successData?.id}/detalhes`, { replace: true })}
                        sx={{
                            bgcolor: '#006a61',
                            borderRadius: '8px',
                            height: 44,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#005a52' },
                        }}
                    >
                        Ver detalhes do encontro
                    </Button>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default MeetingDetails;
